import { logger } from "@services/workers/helpers";
import ObjectsOperations from "@utils/objects/operations";

/**
 * Memoization is a technique for optimizing the performance of a function by caching its
 * results. This is useful when you have a function that is expensive to compute, and you
 * would like to avoid computing it multiple times. This class is a simple implementation
 * of a memoization cache. It uses interval timers to determine when to clear the cache.
 * @class Memoization
 * @author Adam Rzymski
 * @date 2022-03-31
 * @version 1.0.0
 * @property {number} _interval - The interval in milliseconds to clear the cache.
 * @property {Map<string, any>} _cache - The cache.
 * @property {number} _timer - The interval timer.
 * @property {boolean} _isRunning - Whether the timer is running.
 * @property {boolean} _isDisposed - Whether the cache has been disposed.
 * @property {boolean} _isDisposing - Whether the cache is disposing.
 * @property {boolean} _isClearing - Whether the cache is clearing.
 * @property {boolean} _isCleared - Whether the cache has been cleared.
 *
 * @method constructor
 * @param {number} [interval=30 * 60 * 1000] - The interval in milliseconds to clear the cache.
 * @returns {void}
 *
 * @method start - Starts the timer initiating the cache and clearing any expired items.
 * @returns {void}
 *
 * @method stop - Stops the timer.
 * @returns {void}
 *
 * @method clear - Clears the key from the cache.
 * @param {string} key - The key to clear.
 * @returns {void}
 *
 * @method clearAll - Clears all keys from the cache.
 * @returns {void}
 *
 * @method dispose - Disposes the cache.
 * @returns {void}
 *
 * @method get - Gets the value from the cache. If the value is not in the cache, it will be
 * @async {Promise<any>}
 * computed and added to the cache.
 * @param {Function} callback - The callback to compute the value.
 * @returns {any}
 *
 * @method isExpired - Checks if the key is expired.
 * @private
 * @param {number} time - The time to check against.
 *
 */
class Memoization {
  // clear the cache
  private _cache: Map<string, any>;
  private _interval: number;
  private _timer: any;
  private _isRunning: boolean;
  private _isDisposed = false;
  private _isDisposing = false;
  private _isClearing = false;
  private _isCleared = false;
  private _duration = 0;

  /**
   *
   * @param interval - The interval in milliseconds to clear the cache.
   * @returns {void}
   * @memberof Memoization
   * @constructor
   */
  constructor(interval = 60 * 1000, duration = 30 * 60 * 1000) {
    this._cache = new Map();
    this._interval = interval;
    this._isRunning = false;
    this._isDisposed = false;
    this._isDisposing = false;
    this._isClearing = false;
    this._isCleared = false;
    this._duration = duration;
    this.start();
  }

  /**
   * Starts the timer initiating the cache and clearing any expired keys.
   * @returns {void}
   * @memberof Memoization
   * @method start
   * @public
   * @instance
   */
  public start() {
    this._isRunning = true;
    this.clearInterval();
    this.clearExpired();
    this._isDisposed = false;
    this._isDisposing = false;
    this._isClearing = false;
    this._isCleared = false;
    this._timer = setInterval(() => {
      this.clearExpired();
    }, this._interval);
  }

  /**
   * Stops the timer.
   * @returns {void}
   * @memberof Memoization
   * @method stop
   * @public
   * @instance
   */
  public stop() {
    this._isRunning = false;
    this.clearInterval();
  }

  /**
   * Gets the value from the cache. If the value is not in the cache, it will be computed and
   * added to the cache.
   * @async
   * @param {Function} callback - The callback to compute the value.
   * @param {string} key - The key to get the value from.
   * @returns {any}
   * @memberof Memoization
   * @method get
   * @public
   * @instance
   *
   * @example
   * const cache = new Memoization();
   * const getValue = async () => {
   *  return 'value';
   * };
   * const value = await cache.get('key', getValue);
   * logger.debug("Memoization",value);
   * // value
   * // => 'value'
   */
  public async get<T>(key: string, callback: any): Promise<T> {
    // if cache is disposed throw error
    if (this._isDisposed) {
      throw new Error("Memoization has been disposed.");
    }
    // if the key is in the store
    if (this._cache.has(key)) {
      // if the key is not expired
      if (!this.isExpired(this._cache.get(key).expiration)) {
        // increment the hits
        this._cache.get(key).hits++;

        // add last hit time
        this._cache.get(key).lastHit = Date.now();

        logger.debug("Memoization", `Cache: using cached value for key: ${key}`);
        logger.debug("Memoization", `Cache: hit for key: ${key}`);
        logger.debug("Memoization", `Cache: hits: ${this._cache.get(key).hits}`);
        logger.debug("Memoization", `Cache: last hit: ${this._cache.get(key).lastHit}`);
        logger.debug("Memoization", `Cache: expiration as date: ${new Date(this._cache.get(key).expiration)}`);
        logger.debug("Memoization", `Cache: value: `, ObjectsOperations.deepCopyNestedObjectExpensive(this._cache.get(key).value));

        // return the value
        return this._cache.get(key).value;

        // if the key is expired
      } else {
        // remove the key from the store
        this._cache.delete(key);

        // compute the value
        const value = await callback();

        // set the value in the store
        this.set(key, value);

        logger.debug("Memoization", `Cache: using computed value for key: ${key} because it was expired`);
        logger.debug("Memoization", `Cache: hit for key: ${key}`);
        logger.debug("Memoization", `Cache: hits: ${this._cache.get(key).hits}`);
        logger.debug("Memoization", `Cache: last hit: ${this._cache.get(key).lastHit}`);
        logger.debug("Memoization", `Cache: expiration as date: ${new Date(this._cache.get(key).expiration)}`);
        logger.debug("Memoization", `Cache: value: `, ObjectsOperations.deepCopyNestedObjectExpensive(this._cache.get(key).value));

        // return the value
        return value.value;
      }

      // if the key is not in the store
    } else {
      // call the callback
      const value = await callback();
      // set the key to the value
      this.set(key, value);

      logger.debug("Memoization", `Cache: using computed value for key: ${key} because it was not in the cache`);
      logger.debug("Memoization", `Cache: hit for key: ${key}`);
      logger.debug("Memoization", `Cache: hits: ${this._cache.get(key).hits}`);
      logger.debug("Memoization", `Cache: last hit: ${this._cache.get(key).lastHit}`);
      logger.debug("Memoization", `Cache: expiration as date: ${new Date(this._cache.get(key).expiration)}`);
      logger.debug("Memoization", `Cache: value: `, ObjectsOperations.deepCopyNestedObjectExpensive(this._cache.get(key).value));

      // return the value
      return value;
    }
  }

  /**
   * Checks if the key is expired.
   * @private
   * @param {number} time - The time to check against.
   * @returns {boolean}
   * @memberof Memoization
   * @method isExpired
   * @public
   * @instance
   * @example
   * const cache = new Memoization();
   * const isExpired = cache.isExpired(Date.now());
   * logger.debug("Memoization",isExpired);
   * // false
   */
  private isExpired(time: number) {
    return time < Date.now();
  }

  /**
   * Sets the key to the value.
   * @private
   * @param {string} key - The key to set.
   * @param {any} value - The value to set.
   * @returns {void}
   * @memberof Memoization
   * @method set
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * logger.debug("Memoization",cache._cache.get('key'));
   * // { value: 'value', expiration: 1585691200000 }
   */
  public set(key: string, value: any) {
    this._cache.set(key, {
      value,
      hits: 0,
      lastHit: Date.now(),
      expiration: Date.now() + this._duration,
    });
  }

  /**
   * Clears the key from the cache.
   * @param {string} key - The key to clear.
   * @returns {void}
   * @memberof Memoization
   * @method clear
   * @public
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * cache.clear('key');
   * logger.debug("Memoization",cache._cache.has('key'));
   * // false
   */
  public clear(key: string) {
    this._cache.delete(key);
  }

  /**
   * Clears all keys from the cache.
   * @returns {void}
   * @memberof Memoization
   * @method clearAll
   * @private
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * cache.clearAll();
   * logger.debug("Memoization",cache._cache.has('key'));
   * // false
   * logger.debug("Memoization",cache._cache.size);
   * // 0
   */
  private clearAll() {
    this._isClearing = true;
    this._cache.clear();
    this._isClearing = false;
    this._isCleared = true;
  }

  /**
   * Clears all expired keys from the cache.
   * @returns {void}
   * @memberof Memoization
   * @method clearExpired
   * @private
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * cache.clearExpired();
   * logger.debug("Memoization",cache._cache.has('key'));
   * // true
   * logger.debug("Memoization",cache._cache.size);
   * // 1
   * logger.debug("Memoization",cache._cache.get('key'));
   * // { value: 'value', expiration: 1585691200000 }
   * cache.clearExpired();
   * logger.debug("Memoization",cache._cache.has('key'));
   * // false
   * logger.debug("Memoization",cache._cache.size);
   * // 0
   */
  private clearExpired() {
    if (this._isDisposed) {
      return;
    }
    if (this._isDisposing) {
      return;
    }
    this._isClearing = true;
    const keys = Array.from(this._cache.keys());
    for (const key of keys) {
      if (this.isExpired(this._cache.get(key).expiration)) {
        this._cache.delete(key);
      }
    }
    this._isClearing = false;
  }

  /**
   * Clears the interval.
   * @returns {void}
   * @memberof Memoization
   * @method clearInterval
   * @public
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * cache.clearInterval();
   * logger.debug("Memoization",cache._interval);
   * // 0
   * logger.debug("Memoization",cache._cache.has('key'));
   * // true
   * logger.debug("Memoization",cache._cache.size);
   * // 1
   */
  public clearInterval() {
    clearInterval(this._timer);
  }

  /**
   * Disposes the cache.
   * @returns {void}
   * @memberof Memoization
   * @method dispose
   * @public
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * cache.dispose();
   * logger.debug("Memoization",cache._cache.has('key'));
   * // false
   * logger.debug("Memoization",cache._cache.size);
   * // 0
   */
  public dispose() {
    this._isDisposing = true;
    this.stop();
    this.disposeTimer();
    this.clearAll();
    this._isDisposed = true;
    this._isDisposing = false;
  }

  /**
   * Disposes the timer.
   * @returns {void}
   * @memberof Memoization
   * @method disposeTimer
   * @public
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * cache.disposeTimer();
   * logger.debug("Memoization",cache._timer);
   * // null
   * logger.debug("Memoization",cache._cache.has('key'));
   * // true
   * logger.debug("Memoization",cache._cache.size);
   * // 1
   * logger.debug("Memoization",cache._cache.get('key'));
   * // { value: 'value', expiration: 1585691200000 }
   */
  public disposeTimer() {
    this.clearInterval();
  }

  /**
   * Check if cache is running.
   * @returns {boolean}
   * @memberof Memoization
   * @property isRunning
   * @public
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * logger.debug("Memoization",cache.isRunning);
   * // false
   * cache.start();
   * logger.debug("Memoization",cache.isRunning);
   * // true
   * cache.stop();
   * logger.debug("Memoization",cache.isRunning);
   * // false
   * cache.dispose();
   * logger.debug("Memoization",cache.isRunning);
   * // false
   */
  public get isRunning(): boolean {
    return this._isRunning;
  }

  /**
   * Returns the cache.
   * @returns {Map<string, any>}
   * @memberof Memoization
   * @property cache
   * @public
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * logger.debug("Memoization",cache.cache);
   * // Map { 'key' => { value: 'value', expiration: 1585691200000 } }
   * cache.dispose();
   * logger.debug("Memoization",cache.cache);
   * // Map {}
   */
  public get cache(): Map<string, any> {
    return this._cache;
  }

  /**
   * Returns the interval.
   * @returns {number}
   * @memberof Memoization
   * @property interval
   * @public
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * logger.debug("Memoization",cache.interval);
   * // 0
   * cache.start();
   * logger.debug("Memoization",cache.interval);
   * // 1000
   * cache.stop();
   * logger.debug("Memoization",cache.interval);
   * // 0
   */
  public get interval(): number {
    return this._interval;
  }

  /**
   * Returns the timer.
   * @returns {number}
   * @memberof Memoization
   * @property timer
   * @public
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * logger.debug("Memoization",cache.timer);
   * // null
   * cache.start();
   * logger.debug("Memoization",cache.timer);
   * // 4654651
   * cache.stop();
   * logger.debug("Memoization",cache.timer);
   * // null
   */
  public get timer(): number {
    return this._timer;
  }

  /**
   * Returns if cache is disposed.
   * @returns {boolean}
   * @memberof Memoization
   * @property isDisposed
   * @public
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * logger.debug("Memoization",cache.isDisposed);
   * // false
   * cache.dispose();
   * logger.debug("Memoization",cache.isDisposed);
   * // true
   */
  public get isDisposed(): boolean {
    return this._isDisposed;
  }

  /**
   * Returns if cache is disposing.
   * @returns {boolean}
   * @memberof Memoization
   * @property isDisposing
   * @public
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * logger.debug("Memoization",cache.isDisposing);
   * // false
   * cache.dispose();
   * logger.debug("Memoization",cache.isDisposing);
   * // true
   */
  public get isDisposing(): boolean {
    return this._isDisposing;
  }

  /**
   * Returns the cache size.
   * @returns {number}
   * @memberof Memoization
   * @property size
   * @public
   * @example
   * const cache = new Memoization();
   * cache.set('key', 'value');
   * logger.debug("Memoization",cache.size);
   * // 1
   * cache.dispose();
   * logger.debug("Memoization",cache.size);
   * // 0
   */
  public get size(): number {
    return this._cache.size;
  }
}

// assign the memoization class to the memoization variable and store on window
const memoization = new Memoization();

// declare new variable for the memoization variable on window
declare global {
  interface Window {
    Memoization: Memoization;
  }
}

window.Memoization = memoization;

// export as default
export default memoization;
